import { BusinessHours, DayOfWeek, SpecialClosing } from '@sugg-gestion/ubidreams-react-suggpro'
import React, { useEffect, useMemo, useState } from 'react'
import { useStyles } from './styles.css'
import moment, { Moment } from 'moment'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    useMediaQuery,
    useTheme,
} from '@material-ui/core'
import clsx from 'clsx'
import { useColorByName } from '../../helpers/useColorByName'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import SubmitButton from '../../common/submitButton'
import { useTranslation } from 'react-i18next'

type ReservationSlot = {
    date: string // YYYY-MM-DD
    dayOfWeek: DayOfWeek
    start: string // HH:mm (targeted time slot)
    end: string // HH:mm
}

interface Props {
    fullDays: Array<string>
    businessHours: BusinessHours | null
    specialClosing: SpecialClosing[]
    onChangeFullDays?: (next: string[]) => void
    bookingInProgress: boolean
}

/**
 * Splits an array into chunks of a specified size
 * @param arr - The array to split
 * @param size - The size of each chunk
 * @returns Array of chunks
 */
const chunk = <T,>(arr: T[], size: number): T[][] => {
    const res: T[][] = []
    for (let i = 0; i < arr.length; i += size) res.push(arr.slice(i, i + size))
    return res
}

/**
 * Capitalizes the first letter of a string
 * @param s - The string to capitalize
 * @returns The capitalized string
 */
const capitalize = (s: string) => (s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s)

/**
 * Converts ISO weekday number (1-7, Monday-Sunday) to DayOfWeek enum
 * @param iso - ISO weekday number (1 = Monday, 7 = Sunday)
 * @returns Corresponding DayOfWeek enum value
 */
const isoWeekdayToDayOfWeek = (iso: number): DayOfWeek => {
    switch (iso) {
        case 1:
            return DayOfWeek.Monday
        case 2:
            return DayOfWeek.Tuesday
        case 3:
            return DayOfWeek.Wednesday
        case 4:
            return DayOfWeek.Thursday
        case 5:
            return DayOfWeek.Friday
        case 6:
            return DayOfWeek.Saturday
        case 7:
        default:
            return DayOfWeek.Sunday
    }
}

/**
 * Generates a unique key for a reservation slot
 * @param slot - The reservation slot
 * @returns String key in format "YYYY-MM-DD.HH:mm-HH:mm"
 */
const slotKey = (slot: ReservationSlot) => `${slot.date}.${slot.start}-${slot.end}`

/**
 * Normalizes fullDays entry to a consistent format
 * Supports both old format (ISO string with timezone) and new format (YYYY-MM-DD.HH:mm-HH:mm)
 * @param raw - The raw fullDays entry string
 * @returns Normalized string or null if invalid
 */
const normalizeFullDaysEntry = (raw: string): string | null => {
    if (!raw) return null

    // New format: "YYYY-MM-DD.HH:mm-HH:mm" => keep as is
    if (raw.includes('.')) return raw

    // Old format: ISO string with timezone => normalize to "YYYY-MM-DD"
    const m = moment(raw)
    if (!m.isValid()) return null
    return m.format('YYYY-MM-DD')
}

/**
 * Calendar component for managing booking availability
 * Displays a calendar grid with time slots that can be opened or closed for bookings
 * Supports business hours, special closings, and responsive mobile/desktop layouts
 * @param props - Component props
 */
const BookingCalendar: React.FC<Props> = ({
    fullDays,
    businessHours,
    specialClosing,
    onChangeFullDays,
    bookingInProgress,
}) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

    // Mobile view shows 3 days in 3 columns, desktop shows 28 days (4 weeks) in 7 columns
    const daysToShow = isMobile ? 3 : 28
    const columns = isMobile ? 3 : 7

    // Calculate default view start: today for mobile, or start of previous week for desktop
    const defaultViewStartISO = useMemo(() => {
        const now = moment().startOf('day')
        return isMobile
            ? now.format('YYYY-MM-DD')
            : now.clone().startOf('isoWeek').subtract(1, 'week').format('YYYY-MM-DD')
    }, [isMobile])

    const [viewStartISO, setViewStartISO] = useState(() => defaultViewStartISO)

    // Si on passe desktop <-> mobile, on revient sur l’affichage par défaut adapté
    React.useEffect(() => {
        setViewStartISO(defaultViewStartISO)
    }, [defaultViewStartISO])

    const viewStart = useMemo(() => moment(viewStartISO, 'YYYY-MM-DD').startOf('day'), [viewStartISO])
    const viewEnd = useMemo(() => viewStart.clone().add(daysToShow - 1, 'day'), [viewStart, daysToShow])

    const goPrev = () =>
        setViewStartISO((iso) => moment(iso, 'YYYY-MM-DD').subtract(daysToShow, 'day').format('YYYY-MM-DD'))

    const goNext = () =>
        setViewStartISO((iso) => moment(iso, 'YYYY-MM-DD').add(daysToShow, 'day').format('YYYY-MM-DD'))

    const goToday = () => setViewStartISO(defaultViewStartISO)
    const isDefaultView = viewStartISO === defaultViewStartISO

    const periodLabel = useMemo(() => {
        moment.locale('fr')
        const left = viewStart.format(isMobile ? 'D MMM' : 'D MMMM YYYY')
        const right = viewEnd.format(isMobile ? 'D MMM' : 'D MMMM YYYY')
        return t('views.booking.calendar.period', { start: left, end: right })
    }, [viewStart, viewEnd, isMobile, t])

    const { headerDays, weeks } = useMemo(() => {
        moment.locale('fr')

        const days: Moment[] = Array.from({ length: daysToShow }, (_, i) => viewStart.clone().add(i, 'day'))
        const weeksLocal = chunk(days, columns) // 3 colonnes en mobile, sinon 7

        return { headerDays: weeksLocal[0] ?? [], weeks: weeksLocal }
    }, [viewStart, daysToShow, columns])

    // Set of full day closures in "YYYY-MM-DD" format (entire day closed)
    const fullDaysDaySet = useMemo(() => {
        const set = new Set<string>()
        for (const raw of fullDays ?? []) {
            const normalized = normalizeFullDaysEntry(raw)
            if (!normalized) continue
            if (!normalized.includes('.')) set.add(normalized) // only days "YYYY-MM-DD"
        }
        return set
    }, [fullDays])

    // Set of specific time slot closures in "YYYY-MM-DD.HH:mm-HH:mm" format
    const fullDaysSlotSet = useMemo(() => {
        const set = new Set<string>()
        for (const raw of fullDays ?? []) {
            const normalized = normalizeFullDaysEntry(raw)
            if (!normalized) continue
            if (normalized.includes('.')) set.add(normalized) // only time slots
        }
        return set
    }, [fullDays])

    /**
     * Formats day number with optional month and year display
     * Shows month on first cell or first day of month, shows year on first week of year
     * @param d - The moment date
     * @param isFirstCell - Whether this is the first cell in the grid
     * @returns Formatted day string
     */
    const formatDayNumberWithOptionalMonth = (d: Moment, isFirstCell: boolean) => {
        const showMonth = isFirstCell || d.date() === 1
        const showYear = d.date() === 1 && d.week() === 1
        const dayWithMonth = showMonth ? `${d.format('D')} ${capitalize(formatMonth(d))}` : d.format('D')
        return showYear ? `${dayWithMonth} ${d.format('YYYY')}` : dayWithMonth
    }

    const today = useMemo(() => moment().startOf('day'), [])

    /**
     * Checks if a given date is today
     * @param d - The moment date to check
     * @returns True if the date is today
     */
    const isToday = (d: Moment) => d.clone().startOf('day').isSame(today)

    /**
     * Checks if a day is closed according to business hours configuration
     * @param d - The moment date to check
     * @returns True if no business hours are defined for this day
     */
    const isClosedByBusinessHours = (d: Moment) => {
        if (!businessHours) return false
        const dayOfWeek = isoWeekdayToDayOfWeek(d.isoWeekday())
        const hours = businessHours[dayOfWeek] ?? []
        return hours.length === 0
    }

    /**
     * Finds if a day falls within any special closing period
     * @param d - The moment date to check
     * @returns The SpecialClosing object if found, null otherwise
     */
    const getSpecialClosingForDay = (d: Moment): SpecialClosing | null => {
        if (!specialClosing || specialClosing.length === 0) return null
        const day = d.clone().startOf('day')

        for (const sc of specialClosing) {
            const a = moment((sc as any).dateStart).startOf('day')
            const b = moment((sc as any).dateEnd).startOf('day')
            if (!a.isValid() || !b.isValid()) continue

            const start = moment.min(a, b)
            const end = moment.max(a, b)

            if (day.isBetween(start, end, 'day', '[]')) {
                return sc
            }
        }
        return null
    }

    /**
     * Formats month name, abbreviated on mobile, full on desktop
     * @param d - The moment date
     * @returns Capitalized month name
     */
    const formatMonth = (d: Moment) => capitalize(d.format(isMobile ? 'MMM' : 'MMMM'))

    /**
     * Formats day of week name, abbreviated on mobile, full on desktop
     * @param d - The moment date
     * @returns Capitalized day of week name
     */
    const formatDow = (d: Moment) => capitalize(d.format(isMobile ? 'ddd' : 'dddd'))

    /**
     * Checks if a slot is closed (either entire day or specific time slot)
     * @param slot - The reservation slot to check
     * @returns True if the slot is closed
     */
    const isSlotClosedByFullDays = (slot: ReservationSlot) => {
        return fullDaysDaySet.has(slot.date) || fullDaysSlotSet.has(slotKey(slot))
    }

    /**
     * Gets all available reservation slots for a given day based on business hours
     * If no services are defined, creates one slot for the entire business hour period
     * If services are defined, creates one slot per service
     * @param d - The moment date
     * @returns Array of reservation slots
     */
    const getSlotsForDay = (d: Moment): ReservationSlot[] => {
        if (!businessHours) return []
        const dayOfWeek = isoWeekdayToDayOfWeek(d.isoWeekday())
        const date = d.format('YYYY-MM-DD')

        const hours = ((businessHours as any)[dayOfWeek] ?? []) as any[]
        if (!Array.isArray(hours) || hours.length === 0) return []

        const slots: ReservationSlot[] = []
        for (const hour of hours) {
            const hourStart = hour?.startTime
            const hourEnd = hour?.endTime
            const services = Array.isArray(hour?.services) ? hour.services : []

            if (!hourStart || !hourEnd) continue

            // If services is empty => the targeted slot is the complete business hour
            if (services.length === 0) {
                slots.push({ date, dayOfWeek, start: hourStart, end: hourEnd })
            } else {
                // Otherwise, 1 button per service (targeted slot = service)
                for (const s of services) {
                    const start = s?.startTime
                    const end = s?.endTime
                    if (!start || !end) continue
                    slots.push({ date, dayOfWeek, start, end })
                }
            }
        }
        return slots
    }

    const [slotDialogOpen, setSlotDialogOpen] = useState(false)
    const [selectedSlot, setSelectedSlot] = useState<ReservationSlot | null>(null)

    const [pendingDialogClose, setPendingDialogClose] = useState(false)

    const openSlotDialog = (slot: ReservationSlot) => {
        setSelectedSlot(slot)
        setSlotDialogOpen(true)
        setPendingDialogClose(false)
    }

    const closeSlotDialog = () => {
        setSlotDialogOpen(false)
        setSelectedSlot(null)
        setPendingDialogClose(false)
    }

    // Auto-close dialog after save operation completes
    useEffect(() => {
        if (!slotDialogOpen) return
        if (!pendingDialogClose) return
        if (bookingInProgress) return
        closeSlotDialog()
    }, [bookingInProgress, pendingDialogClose, slotDialogOpen])

    /**
     * Marks dialog for closure after current save operation completes
     */
    const requestCloseAfterSave = () => setPendingDialogClose(true)

    /**
     * Handles dialog close events, preventing closure during booking operations
     * @param _event - The close event (unused)
     * @param reason - The reason for closing (backdrop click or escape key)
     */
    const handleDialogClose = (_event: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (bookingInProgress) return
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            closeSlotDialog()
            return
        }
        closeSlotDialog()
    }

    /**
     * Updates the closed/open status of a reservation slot
     * @param slot - The reservation slot to update
     * @param closed - True to close the slot, false to open it
     */
    const setSlotClosed = (slot: ReservationSlot, closed: boolean) => {
        const key = slotKey(slot)
        const current = fullDays ?? []

        const next = closed ? Array.from(new Set([...current, key])) : current.filter((x) => x !== key)

        onChangeFullDays?.(next)
    }

    const { generateHSL, HSLtoString } = useColorByName()

    return (
        <div className={classes.calendarRoot}>
            <div className={classes.calendarNav}>
                <IconButton
                    className={classes.calendarNavButton}
                    onClick={goPrev}
                    aria-label={t('views.booking.calendar.prevPeriod')}
                >
                    <ChevronLeftIcon />
                </IconButton>

                <div className={classes.calendarNavRange}>{periodLabel}</div>

                <Button
                    className={classes.calendarNavTodayButton}
                    variant="outlined"
                    onClick={goToday}
                    disabled={isDefaultView}
                >
                    {t('views.booking.calendar.today')}
                </Button>

                <IconButton
                    className={classes.calendarNavButton}
                    onClick={goNext}
                    aria-label={t('views.booking.calendar.nextPeriod')}
                >
                    <ChevronRightIcon />
                </IconButton>
            </div>

            {/* Header: dedicated row (7 columns on desktop, 3 on mobile) */}
            <div className={classes.calendarHeaderGrid}>
                {headerDays.map((d) => {
                    return (
                        <div
                            className={clsx(classes.calendarHeaderCell)}
                            key={`header-${d.format('YYYY-MM-DD')}`}
                        >
                            <div className={clsx(classes.calendarHeaderCellContent)}>
                                <div className={classes.calendarHeaderDow}>{capitalize(formatDow(d))}</div>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Body: 4 weeks (28 cells) in 7-column grid on desktop, 3 days in 3-column grid on mobile */}
            <div className={classes.calendarBodyGrid}>
                {weeks.flat().map((d, i, allDays) => {
                    const isFirstCell = i === 0
                    const cellIsToday = isToday(d)
                    const closedBusiness = isClosedByBusinessHours(d)
                    const special = getSpecialClosingForDay(d)
                    const slots = special ? [] : getSlotsForDay(d)

                    const prev = allDays[i - 1]
                    const next = allDays[i + 1]
                    const monthFormat = 'MMMM-YYYY'
                    const monthKey = d.format(monthFormat)
                    const prevMonthKey = prev ? prev.format(monthFormat) : null
                    const nextMonthKey = next ? next.format(monthFormat) : null

                    const isMonthSegmentStart = prevMonthKey !== monthKey
                    const isMonthSegmentEnd = nextMonthKey !== monthKey

                    const isEndOfLine = i % columns === columns - 1
                    const isStartOfLine = i % columns === 0

                    const monthLabel = capitalize(formatMonth(d))
                    const monthColor = HSLtoString(generateHSL(monthKey))

                    return (
                        <div
                            className={clsx(classes.calendarCell)}
                            key={d.format('YYYY-MM-DD')}
                            title={special?.label ?? undefined}
                        >
                            <div className={classes.calendarMonthColor}>
                                <div
                                    className={clsx(
                                        classes.calendarMonthBar,
                                        isMonthSegmentStart && classes.calendarMonthBarStart,
                                        isMonthSegmentEnd && classes.calendarMonthBarEnd,
                                        isStartOfLine && classes.calendarMonthBarStartOfLine,
                                        isEndOfLine && classes.calendarMonthBarEndOfLine,
                                    )}
                                    style={{ backgroundColor: monthColor, color: monthColor }}
                                >
                                    {isMonthSegmentStart && (
                                        <span className={classes.calendarMonthLabel}>{monthLabel}</span>
                                    )}
                                </div>
                            </div>
                            <div
                                className={clsx(
                                    classes.calendarCellMain,
                                    cellIsToday && classes.calendarToday,
                                    closedBusiness && classes.calendarClosed,
                                    special && classes.calendarSpecialClosed,
                                )}
                            >
                                <div className={classes.calendarCellDate}>
                                    {formatDayNumberWithOptionalMonth(d, isFirstCell)}
                                </div>

                                {special?.label ? (
                                    <div className={classes.calendarSpecialLabel}>{special.label}</div>
                                ) : (
                                    <div className={classes.calendarCellContent}>
                                        {slots.map((slot) => {
                                            const isClosed = isSlotClosedByFullDays(slot)
                                            return (
                                                <Button
                                                    key={slotKey(slot)}
                                                    className={
                                                        isClosed
                                                            ? classes.calendarSlotButtonClose
                                                            : classes.calendarSlotButtonOpen
                                                    }
                                                    size="small"
                                                    variant={isClosed ? 'contained' : 'outlined'}
                                                    color={isClosed ? 'inherit' : 'default'}
                                                    onClick={() => openSlotDialog(slot)}
                                                    disabled={closedBusiness}
                                                >
                                                    {slot.start} – {slot.end}
                                                </Button>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>

            <Dialog open={slotDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="xs">
                {selectedSlot && (
                    <DialogTitle>
                        <strong>{moment(selectedSlot.date).format('DD MMMM')}</strong> : {selectedSlot.start}{' '}
                        – {selectedSlot.end}
                    </DialogTitle>
                )}
                <DialogContent>
                    {selectedSlot && (
                        <div className={classes.calendarDialogSummary}>
                            <div className={classes.calendarDialogStatus}>
                                {t('views.booking.calendar.currentStatus')}{' '}
                                <strong>
                                    {isSlotClosedByFullDays(selectedSlot)
                                        ? t('views.booking.calendar.status.closed')
                                        : t('views.booking.calendar.status.open')}
                                </strong>
                            </div>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={closeSlotDialog} disabled={bookingInProgress}>
                        {t('common.cancel')}
                    </Button>

                    {selectedSlot && !isSlotClosedByFullDays(selectedSlot) && (
                        <SubmitButton
                            loading={bookingInProgress}
                            onClick={() => {
                                setSlotClosed(selectedSlot, true)
                                requestCloseAfterSave()
                            }}
                            color="primary"
                            variant="contained"
                        >
                            {t('views.booking.calendar.actions.closeSlot')}
                        </SubmitButton>
                    )}

                    {selectedSlot && isSlotClosedByFullDays(selectedSlot) && (
                        <SubmitButton
                            loading={bookingInProgress}
                            onClick={() => {
                                setSlotClosed(selectedSlot, false)
                                requestCloseAfterSave()
                            }}
                            color="primary"
                            variant="contained"
                        >
                            {t('views.booking.calendar.actions.openSlot')}
                        </SubmitButton>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    )
}
export default BookingCalendar
